/**
 * Orders Management - Dataverse Hooks
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { IOrderQueryParams } from "../types";
import {
  getOrderListFromDataverse,
  getOrderDetail,
  getTotalOrdersCountApi,
  getTotalOrdersValueApi,
  getOrderCountByStatusApi,
  getAllOrderCountsByStatusApi,
  getOutstandingPaymentsValueApi,
  updateOrderApi,
  fulfillSalesOrderApi,
  cancelSalesOrderApi,
  createPaymentApi,
  ICreatePaymentPayload,
} from "../api/dataverse";
import { queryKey } from "../constants";

/**
 * Hook to fetch a single order detail by ID
 */
export const useOrderDetail = (id: string) => {
  return useQuery({
    queryKey: [queryKey.getOrderDetail, id],
    queryFn: () => getOrderDetail(id),
    enabled: Boolean(id),
  });
};

/**
 * Hook to fetch order list from Dataverse
 */
export const useOrderListFromDataverse = (params: {
  queryParams: IOrderQueryParams;
  enabled: boolean;
}) => {
  return useQuery({
    queryKey: [queryKey.getOrderList, params.queryParams],
    queryFn: () => getOrderListFromDataverse(params.queryParams),
    enabled: params.enabled,
  });
};

/**
 * Hook to get total orders count
 */
export const useTotalOrdersCount = (region?: number | null) => {
  return useQuery({
    queryKey: [queryKey.getTotalOrdersCount, region],
    queryFn: () => getTotalOrdersCountApi(region),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};

/**
 * Hook to get total orders value
 */
export const useTotalOrdersValue = () => {
  return useQuery({
    queryKey: [queryKey.getTotalOrdersValue],
    queryFn: getTotalOrdersValueApi,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};

/**
 * Hook to get order count by status code (single status) — used for the
 * sidebar badge (Order Received count), cheap aggregate query instead of
 * fetching the full list
 */
export const useOrderCountByStatus = (statuscode?: number, enabled: boolean = true, region?: number | null) => {
  return useQuery({
    queryKey: [queryKey.getOrderCountByStatus, statuscode, region],
    queryFn: () => getOrderCountByStatusApi(statuscode, region),
    enabled,
    staleTime: 60 * 1000,
    select: (data) => data?.value?.[0]?.count ?? 0,
  });
};

/**
 * Hook to get all order counts grouped by status code
 */
export const useAllOrderCountsByStatus = (region?: number | null) => {
  return useQuery({
    queryKey: [queryKey.getAllOrderCountsByStatus, region],
    queryFn: () => getAllOrderCountsByStatusApi(region),
  });
};

/**
 * Hook to get outstanding payments value
 */
export const useOutstandingPaymentsValue = (region?: number | null) => {
  return useQuery({
    queryKey: [queryKey.getOutstandingPaymentsValue, region],
    queryFn: () => getOutstandingPaymentsValueApi(region),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};

/**
 * Generic hook to update order - can update any order field(s)
 */
export const useUpdateOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateOrderApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getOrderList] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getOrderDetail] });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getTotalOrdersCount],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getTotalOrdersValue],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getAllOrderCountsByStatus],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getOutstandingPaymentsValue],
      });
      toast.success("Order updated successfully");
    },
  });
};

/**
 * Hook to fulfill sales order (change status to completed)
 */
export const useFulfillSalesOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: fulfillSalesOrderApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getOrderList] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getOrderDetail] });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getTotalOrdersCount],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getTotalOrdersValue],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getAllOrderCountsByStatus],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getOutstandingPaymentsValue],
      });
      toast.success("Order fulfilled successfully");
    },
  });
};

/**
 * Hook to export all orders matching the current filter as CSV data
 */
export const useExportOrders = () => {
  return useMutation({
    mutationFn: (filter: string) =>
      getOrderListFromDataverse({ current: 1, pageSize: 5000, filter }),
  });
};

/**
 * Hook to create a payment record (send payment link)
 */
export const useCreatePayment = () => {
  return useMutation({
    mutationFn: (payload: ICreatePaymentPayload) => createPaymentApi(payload),
  });
};

/**
 * Hook to cancel sales order (change status to cancelled)
 */
export const useCancelSalesOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelSalesOrderApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getOrderList] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getOrderDetail] });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getTotalOrdersCount],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getTotalOrdersValue],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getAllOrderCountsByStatus],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKey.getOutstandingPaymentsValue],
      });
      toast.success("Order cancelled successfully");
    },
  });
};
